import React from 'react';
import { Link, useLoaderData } from '@remix-run/react';
import type { loader as homeLoader } from '~/routes/_app._index';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';
import { HomeCarouselSection } from '@rocco/ui/layout/components/HomeCarouselSection';
import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import { Button as RoccoButton } from '@rocco/components/button';
import { useSetLoginModal } from '~/modules-rocco/user/hook/use-open-login-modal';
import { useTracker } from '@archipro-website/tracker';
import type { TrackingEvent } from '@archipro-website/tracker';

export const HomePageUsLaunchStory = () => {
    const { usLaunchStorySection, appDisplayMode, isLoggedIn } =
        useLoaderData<typeof homeLoader>();

    const { setLoginModal } = useSetLoginModal();
    const tracker = useTracker();

    if (!usLaunchStorySection) {
        return null;
    }

    const log = (eventName: TrackingEvent, chapterStep?: number) => {
        tracker.log(eventName, {
            data: {
                extraData: {
                    sectionName: 'HomePageUsLaunchStory',
                    ...(chapterStep !== undefined
                        ? { chapterStep: String(chapterStep) }
                        : {}),
                },
            },
            url: new URL(window.location.href),
        });
    };

    const trackPrimaryCta = () => log('WatchSeries');

    const handleChapterClick = (chapterStep: number, link: string) => {
        log('WatchSeries', chapterStep);

        if (link.includes('/login') && !isLoggedIn) {
            setLoginModal({
                isOpen: true,
                authSource: 'homeModule',
                redirect: link,
            });
        }
    };

    return (
        <section
            className="bg-brick-700 bg-cover bg-center bg-no-repeat py-20 md:py-55"
            style={
                usLaunchStorySection.backgroundImage
                    ? {
                          backgroundImage: `url(${usLaunchStorySection.backgroundImage})`,
                      }
                    : {}
            }
        >
            <HomeCarouselSection
                displayMode={appDisplayMode}
                title={usLaunchStorySection.title}
                description={usLaunchStorySection.description}
                theme="dark"
                ctaButton={usLaunchStorySection.cta}
                showLoginModal={() =>
                    setLoginModal({
                        isOpen: true,
                        authSource: 'homeModule',
                        redirect: usLaunchStorySection.cta.link,
                    })
                }
                trackClick={trackPrimaryCta}
            >
                {usLaunchStorySection.chapters.map((chapter) => (
                    <div
                        key={`us-launch-story-${chapter.step}`}
                        className={cn(
                            'flex aspect-[280/350] flex-col rounded-2 border-[2px] border-[#c6c6c6] bg-gray-900/20 p-4.5 backdrop-blur-[7.5px] transition-colors duration-300 md:aspect-[594/690] md:h-[43.125rem] md:pb-12 md:pl-15.5 md:pr-[2.8125rem] md:pt-12.5',
                        )}
                    >
                        <div className="flex flex-row justify-between pb-[2.375rem] md:pb-[4.1875rem]">
                            {chapter.image ? (
                                <div className="relative flex aspect-[200/225] w-50 items-center justify-center overflow-hidden bg-gray-300">
                                    <Image
                                        src={chapter.image}
                                        alt={chapter.description}
                                        fill={true}
                                        loading="lazy"
                                        fit="cover"
                                        width={200}
                                        height={225}
                                    />
                                </div>
                            ) : null}

                            <span className="hidden text-xl text-white md:inline md:text-[15.625rem] md:leading-[12.5rem]">
                                {chapter.step}
                            </span>
                        </div>
                        <div className="flex flex-col gap-4 md:gap-12">
                            <span className="h-30 pb-[0.35rem] text-sm leading-[142.85%] text-white md:h-[11.5rem] md:min-h-[11.5rem] md:w-[23.5rem] md:text-lg md:leading-[121.43%]">
                                {chapter.description}
                            </span>

                            <div className="mt-4 md:mt-auto">
                                {chapter.link.includes('/login') &&
                                !isLoggedIn ? (
                                    <RoccoButton
                                        color="white"
                                        inverted
                                        clickAnimation="scale"
                                        onClick={() =>
                                            handleChapterClick(
                                                chapter.step,
                                                chapter.link,
                                            )
                                        }
                                    >
                                        {chapter.ctaText}
                                        <ArrowRight className="ml-1.5 w-4" />
                                    </RoccoButton>
                                ) : (
                                    <RoccoButton
                                        color="white"
                                        inverted
                                        asChild
                                        clickAnimation="scale"
                                        onClick={() =>
                                            log('WatchSeries', chapter.step)
                                        }
                                    >
                                        <Link
                                            to={chapter.link}
                                            prefetch="intent"
                                        >
                                            {chapter.ctaText}
                                            <ArrowRight className="ml-1.5 w-4" />
                                        </Link>
                                    </RoccoButton>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </HomeCarouselSection>
        </section>
    );
};
